import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { ArrowLeft, FileDown, Loader, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportExport = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [reportType, setReportType] = useState('full');

  React.useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].project_id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value}`;
  };

  const generatePDF = async () => {
    if (!selectedProject) {
      toast.error(t('export.toastSelectProject'));
      return;
    }

    setGenerating(true);
    
    try {
      // Fetch all project data
      const [projectRes, kpisRes, progressRes, costsRes, evmRes, deviationsRes] = await Promise.all([
        axios.get(`${API}/projects/${selectedProject}`),
        axios.get(`${API}/projects/${selectedProject}/kpis`),
        axios.get(`${API}/projects/${selectedProject}/progress`),
        axios.get(`${API}/projects/${selectedProject}/costs`),
        axios.get(`${API}/projects/${selectedProject}/evm`),
        axios.get(`${API}/projects/${selectedProject}/deviations`)
      ]);

      const project = projectRes.data;
      const kpis = kpisRes.data;
      const progress = progressRes.data;
      const costs = costsRes.data;
      const evm = evmRes.data;
      const deviations = deviationsRes.data;

      // Create PDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      let yPos = 20;

      // Header
      doc.setFontSize(20);
      doc.setTextColor(220, 38, 38); // Red color for Oracle
      doc.text('ORACLE', 14, yPos);
      doc.setFontSize(12);
      doc.setTextColor(100);
      doc.text('Project Cost Control Report', 50, yPos);
      
      yPos += 10;
      doc.setDrawColor(220, 38, 38);
      doc.setLineWidth(0.5);
      doc.line(14, yPos, pageWidth - 14, yPos);
      
      // Project Info
      yPos += 15;
      doc.setFontSize(14);
      doc.setTextColor(0);
      doc.text(`${project.project_code} - ${project.project_name}`, 14, yPos);
      
      yPos += 8;
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Client: ${project.client}`, 14, yPos);
      yPos += 5;
      doc.text(`Contract Value: ${formatCurrency(project.contract_value_usd)}`, 14, yPos);
      yPos += 5;
      doc.text(`Duration: ${project.start_date} to ${project.end_date}`, 14, yPos);
      yPos += 5;
      doc.text(`Report Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 14, yPos);

      // KPI Summary Section
      yPos += 15;
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text('Key Performance Indicators', 14, yPos);
      yPos += 5;

      doc.autoTable({
        startY: yPos,
        head: [['Metric', 'Planned', 'Actual', 'Variance']],
        body: [
          ['Physical Progress', `${kpis.overall_progress_planned}%`, `${kpis.overall_progress_actual}%`, `${kpis.progress_variance}%`],
          ['Schedule Performance (SPI)', '1.00', kpis.spi.toString(), kpis.spi >= 1 ? 'On Track' : 'Behind'],
          ['Cost Performance (CPI)', '1.00', kpis.cpi.toString(), kpis.cpi >= 1 ? 'Under Budget' : 'Over Budget'],
          ['Budget Spent', '-', formatCurrency(kpis.budget_spent), '-'],
          ['Budget Remaining', '-', formatCurrency(kpis.budget_remaining), '-'],
          ['Forecast at Completion', formatCurrency(kpis.contract_value), formatCurrency(kpis.forecast_at_completion), formatCurrency(kpis.forecast_at_completion - kpis.contract_value)]
        ],
        theme: 'striped',
        headStyles: { fillColor: [220, 38, 38] },
        styles: { fontSize: 9 }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      // EVM Section
      if (reportType === 'full' || reportType === 'evm') {
        doc.setFontSize(12);
        doc.text('Earned Value Management', 14, yPos);
        yPos += 5;

        doc.autoTable({
          startY: yPos,
          head: [['Metric', 'Value', 'Description']],
          body: [
            ['PV (BCWS)', formatCurrency(evm.summary.pv), 'Planned Value'],
            ['EV (BCWP)', formatCurrency(evm.summary.ev), 'Earned Value'],
            ['AC (ACWP)', formatCurrency(evm.summary.ac), 'Actual Cost'],
            ['SV', formatCurrency(evm.summary.sv), 'Schedule Variance (EV-PV)'],
            ['CV', formatCurrency(evm.summary.cv), 'Cost Variance (EV-AC)'],
            ['EAC', formatCurrency(evm.summary.eac), 'Estimate at Completion'],
            ['VAC', formatCurrency(evm.summary.vac), 'Variance at Completion'],
            ['TCPI', evm.summary.tcpi.toString(), 'To Complete Performance Index']
          ],
          theme: 'striped',
          headStyles: { fillColor: [99, 102, 241] },
          styles: { fontSize: 9 }
        });

        yPos = doc.lastAutoTable.finalY + 15;
      }

      // Progress by Discipline
      if (reportType === 'full' || reportType === 'progress') {
        // Check if need new page
        if (yPos > 220) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(12);
        doc.text('Progress by Discipline', 14, yPos);
        yPos += 5;

        doc.autoTable({
          startY: yPos,
          head: [['Discipline', 'Weight', 'Planned', 'Actual', 'Variance']],
          body: progress.disciplines.map(d => [
            d.discipline,
            `${d.weight}%`,
            `${d.planned}%`,
            `${d.actual}%`,
            `${d.variance}%`
          ]),
          theme: 'striped',
          headStyles: { fillColor: [59, 130, 246] },
          styles: { fontSize: 9 }
        });

        yPos = doc.lastAutoTable.finalY + 15;
      }

      // Deviations & Risks
      if ((reportType === 'full' || reportType === 'deviations') && deviations.length > 0) {
        // Check if need new page
        if (yPos > 200) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(12);
        doc.text('Active Deviations & Risks', 14, yPos);
        yPos += 5;

        doc.autoTable({
          startY: yPos,
          head: [['Area', 'Description', 'Severity', 'Status']],
          body: deviations.map(d => [
            d.area,
            d.description.substring(0, 50) + '...',
            d.severity.toUpperCase(),
            d.status.replace('_', ' ')
          ]),
          theme: 'striped',
          headStyles: { fillColor: [239, 68, 68] },
          styles: { fontSize: 9 },
          columnStyles: {
            1: { cellWidth: 80 }
          }
        });
      }

      // Footer on all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Page ${i} of ${pageCount} | Oracle Project Cost Control | Generated on ${new Date().toLocaleString()}`,
          pageWidth / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }

      // Save PDF
      const fileName = `${project.project_code}_CostControl_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('export.toastError'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="pb-20 bg-gray-50 min-h-screen" data-testid="report-export-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/settings')} className="p-1">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{t('export.title')}</h1>
            <p className="text-xs text-gray-500">{t('export.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Project Selection */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('export.selectProject')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('common.selectProject')} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.project_id} value={project.project_id}>
                    {project.project_code} - {project.project_name.substring(0, 30)}...
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Report Type Selection */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('export.reportType')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('export.selectReportType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full">{t('export.fullReport')}</SelectItem>
                <SelectItem value="evm">{t('export.evmReport')}</SelectItem>
                <SelectItem value="progress">{t('export.progressReport')}</SelectItem>
                <SelectItem value="deviations">{t('export.deviationsReport')}</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Report Contents Preview */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('export.reportContents')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              {reportType === 'full' && (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span>{t('export.projectInfo')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span>{t('export.evmContent')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span>{t('export.progressContent')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span>{t('export.deviationsContent')}</span>
                  </div>
                </>
              )}
              {reportType === 'evm' && (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span>{t('export.projectInfo')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span>{t('export.evmContent')}</span>
                  </div>
                </>
              )}
              {reportType === 'progress' && (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span>{t('export.projectInfo')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span>{t('export.progressContent')}</span>
                  </div>
                </>
              )}
              {reportType === 'deviations' && (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span>{t('export.projectInfo')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-600" />
                    <span>{t('export.deviationsContent')}</span>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Generate Button */}
        <Button 
          onClick={generatePDF}
          disabled={generating || !selectedProject}
          className="w-full bg-red-600 hover:bg-red-700"
          data-testid="generate-pdf-btn"
        >
          {generating ? (
            <>
              <Loader size={16} className="mr-2 animate-spin" />
              {t('export.generating')}
            </>
          ) : (
            <>
              <FileDown size={16} className="mr-2" />
              {t('export.generatePdf')}
            </>
          )}
        </Button>

        {/* Info */}
        <p className="text-xs text-gray-500 text-center">
          {t('export.downloadNote')}
        </p>
      </div>
    </div>
  );
};

export default ReportExport;
